/**
 * Client Color Palette Generator
 * Generates a complete brand color palette from a single primary color
 */

export interface ColorPalette {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  secondary: string;
  secondaryLight: string;
  secondaryDark: string;
  accent: string;
  accentLight: string;
  accentDark: string;
  neutral: string;
  neutralLight: string;
  neutralDark: string;
  success: string;
  warning: string;
  error: string;
  textPrimary: string;
  textSecondary: string;
  background: string;
  surface: string;
}

/**
 * Convert hex color to HSL
 */
function hexToHSL(hex: string): { h: number; s: number; l: number } {
  // Remove # if present
  hex = hex.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Convert HSL to hex color
 */
function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x;
  }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Generate a lighter variant of a color
 */
function lightenColor(hex: string, amount: number): string {
  const hsl = hexToHSL(hex);
  const newL = Math.min(100, hsl.l + amount);
  return hslToHex(hsl.h, hsl.s, newL);
}

/**
 * Generate a darker variant of a color
 */
function darkenColor(hex: string, amount: number): string {
  const hsl = hexToHSL(hex);
  const newL = Math.max(0, hsl.l - amount);
  return hslToHex(hsl.h, hsl.s, newL);
}

/**
 * Generate secondary color (analogous harmony - 30 degrees on color wheel)
 */
function generateSecondaryColor(hex: string): string {
  const hsl = hexToHSL(hex);
  const newH = (hsl.h + 30) % 360;
  return hslToHex(newH, hsl.s, hsl.l);
}

/**
 * Generate accent color (complementary harmony - 180 degrees on color wheel)
 */
function generateAccentColor(hex: string): string {
  const hsl = hexToHSL(hex);
  const newH = (hsl.h + 180) % 360;
  return hslToHex(newH, hsl.s, hsl.l);
}

/**
 * Generate neutral color (desaturated version of primary)
 */
function generateNeutralColor(hex: string): string {
  const hsl = hexToHSL(hex);
  return hslToHex(hsl.h, 10, 50); // Low saturation, medium lightness
}

/**
 * Check if color is light or dark (for text contrast)
 */
function isLightColor(hex: string): boolean {
  const hsl = hexToHSL(hex);
  return hsl.l > 50;
}

/**
 * Main function: Generate complete color palette from primary color
 */
export function generateColorPalette(primaryColor: string): ColorPalette {
  // Ensure primary color has # prefix
  if (!primaryColor.startsWith('#')) {
    primaryColor = '#' + primaryColor;
  }

  // Generate primary variants
  const primary = primaryColor;
  const primaryLight = lightenColor(primary, 20);
  const primaryDark = darkenColor(primary, 20);

  // Generate secondary color and variants (analogous harmony)
  const secondary = generateSecondaryColor(primary);
  const secondaryLight = lightenColor(secondary, 20);
  const secondaryDark = darkenColor(secondary, 20);

  // Generate accent color and variants (complementary harmony)
  const accent = generateAccentColor(primary);
  const accentLight = lightenColor(accent, 20);
  const accentDark = darkenColor(accent, 20);

  // Generate neutral colors
  const neutral = generateNeutralColor(primary);
  const neutralLight = lightenColor(neutral, 30);
  const neutralDark = darkenColor(neutral, 30);

  // Status colors (universal, but adjusted to harmonize)
  const success = '#10B981'; // Green
  const warning = '#F59E0B'; // Orange
  const error = '#EF4444';   // Red

  // Text colors based on primary color lightness
  const textPrimary = '#1F2937';   // Dark gray
  const textSecondary = '#6B7280'; // Medium gray

  // Background colors
  const background = '#FFFFFF';    // White
  const surface = '#F9FAFB';       // Light gray

  return {
    primary,
    primaryLight,
    primaryDark,
    secondary,
    secondaryLight,
    secondaryDark,
    accent,
    accentLight,
    accentDark,
    neutral,
    neutralLight,
    neutralDark,
    success,
    warning,
    error,
    textPrimary,
    textSecondary,
    background,
    surface
  };
}

/**
 * Generate multiple palette options (different color harmonies)
 */
export interface PaletteOption {
  name: string;
  description: string;
  palette: ColorPalette;
}

export function generatePaletteOptions(primaryColor: string): PaletteOption[] {
  const hsl = hexToHSL(primaryColor);

  return [
    {
      name: 'Analogous',
      description: 'Harmonious colors next to each other on the color wheel',
      palette: generateColorPalette(primaryColor)
    },
    {
      name: 'Complementary',
      description: 'Bold contrast with opposite colors',
      palette: {
        ...generateColorPalette(primaryColor),
        secondary: generateAccentColor(primaryColor),
        secondaryLight: lightenColor(generateAccentColor(primaryColor), 20),
        secondaryDark: darkenColor(generateAccentColor(primaryColor), 20)
      }
    },
    {
      name: 'Triadic',
      description: 'Balanced with three evenly spaced colors',
      palette: {
        ...generateColorPalette(primaryColor),
        secondary: hslToHex((hsl.h + 120) % 360, hsl.s, hsl.l),
        secondaryLight: lightenColor(hslToHex((hsl.h + 120) % 360, hsl.s, hsl.l), 20),
        secondaryDark: darkenColor(hslToHex((hsl.h + 120) % 360, hsl.s, hsl.l), 20),
        accent: hslToHex((hsl.h + 240) % 360, hsl.s, hsl.l),
        accentLight: lightenColor(hslToHex((hsl.h + 240) % 360, hsl.s, hsl.l), 20),
        accentDark: darkenColor(hslToHex((hsl.h + 240) % 360, hsl.s, hsl.l), 20)
      }
    },
    {
      name: 'Monochromatic',
      description: 'Professional single-color palette with shades',
      palette: {
        primary: primaryColor,
        primaryLight: lightenColor(primaryColor, 30),
        primaryDark: darkenColor(primaryColor, 30),
        secondary: lightenColor(primaryColor, 15),
        secondaryLight: lightenColor(primaryColor, 40),
        secondaryDark: darkenColor(primaryColor, 15),
        accent: darkenColor(primaryColor, 20),
        accentLight: lightenColor(primaryColor, 10),
        accentDark: darkenColor(primaryColor, 40),
        neutral: generateNeutralColor(primaryColor),
        neutralLight: lightenColor(generateNeutralColor(primaryColor), 30),
        neutralDark: darkenColor(generateNeutralColor(primaryColor), 30),
        success: '#10B981',
        warning: '#F59E0B',
        error: '#EF4444',
        textPrimary: '#1F2937',
        textSecondary: '#6B7280',
        background: '#FFFFFF',
        surface: '#F9FAFB'
      }
    }
  ];
}

/**
 * Export palette as CSS variables
 */
export function paletteToCSS(palette: ColorPalette): string {
  return `
:root {
  --color-primary: ${palette.primary};
  --color-primary-light: ${palette.primaryLight};
  --color-primary-dark: ${palette.primaryDark};
  --color-secondary: ${palette.secondary};
  --color-secondary-light: ${palette.secondaryLight};
  --color-secondary-dark: ${palette.secondaryDark};
  --color-accent: ${palette.accent};
  --color-accent-light: ${palette.accentLight};
  --color-accent-dark: ${palette.accentDark};
  --color-neutral: ${palette.neutral};
  --color-neutral-light: ${palette.neutralLight};
  --color-neutral-dark: ${palette.neutralDark};
  --color-success: ${palette.success};
  --color-warning: ${palette.warning};
  --color-error: ${palette.error};
  --color-text-primary: ${palette.textPrimary};
  --color-text-secondary: ${palette.textSecondary};
  --color-background: ${palette.background};
  --color-surface: ${palette.surface};
}
  `.trim();
}

/**
 * Export palette as JSON
 */
export function paletteToJSON(palette: ColorPalette): string {
  return JSON.stringify(palette, null, 2);
}
